import { z } from 'zod';

// BboxCoordinates schema - passthrough allows extra properties
export const BboxCoordinatesSchema = z
  .object({
    x0: z.number(),
    y0: z.number(),
    x1: z.number(),
    y1: z.number(),
  })
  .passthrough();

// FabricJsCoordinates schema - passthrough allows extra properties
export const FabricJsCoordinatesSchema = z
  .object({
    left: z.number(),
    top: z.number(),
    width: z.number(),
    height: z.number(),
    originX: z.string().optional(),
    originY: z.string().optional(),
  })
  .passthrough();

// SourceRegion schema
export const SourceRegionSchema = z
  .object({
    page: z.number(),
    bbox: BboxCoordinatesSchema,
    fabricjs: FabricJsCoordinatesSchema,
    rotation_deg: z.number().optional(),
  })
  .passthrough();

// FieldLocation schema - passthrough allows extra properties
export const FieldLocationSchema = z
  .object({
    page: z.number(),
    bbox: BboxCoordinatesSchema,
    fabricjs: FabricJsCoordinatesSchema,
    source_regions: z.array(SourceRegionSchema).optional(),
  })
  .passthrough();

// Empty location (for null/undefined location fields)
export const EmptyOrFieldLocationSchema = z.union([FieldLocationSchema, z.object({}).passthrough(), z.null(), z.undefined()]);

// =====================================================
// New Array-Based Schema Format (Document Extraction)
// =====================================================

// Extracted field interface for type safety
export interface ExtractedFieldType {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  description?: string | null;
  verificationLabel?: string | null;
  required?: boolean;
  value?: string | number | boolean | null;
  location?: FieldLocation | Record<string, unknown> | null;
  properties?: ExtractedFieldType[];
  [key: string]: unknown; // Allow additional properties
}

// Recursive schema definition using z.lazy for self-reference
// Using a more permissive schema to handle variations in AI output
const createExtractedFieldSchema = (): z.ZodType<ExtractedFieldType> => {
  const schema: z.ZodType<ExtractedFieldType> = z.lazy(() =>
    z
      .object({
        name: z.string(),
        type: z.enum(['string', 'number', 'boolean', 'object', 'array']),
        description: z.string().nullish(),
        verificationLabel: z.string().nullish(),
        required: z.boolean().optional(),
        value: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
        location: z
          .union([
            z.object({
              page: z.number(),
              bbox: z.record(z.number()),
              fabricjs: z.record(z.union([z.number(), z.string()])),
            }),
            z.record(z.unknown()),
            z.null(),
          ])
          .optional(),
        properties: z.array(schema).optional(),
      })
      .passthrough()
  );
  return schema;
};

export const ExtractedFieldSchema = createExtractedFieldSchema();

// The main schema: array of extracted fields
export const ExtractedFieldsArraySchema = z.array(ExtractedFieldSchema);

// Inferred types from schemas
export type BboxCoordinates = z.infer<typeof BboxCoordinatesSchema>;
export type FabricJsCoordinates = z.infer<typeof FabricJsCoordinatesSchema>;
export type SourceRegion = z.infer<typeof SourceRegionSchema>;
export type FieldLocation = z.infer<typeof FieldLocationSchema>;
export type ExtractedField = ExtractedFieldType;

// ParsedField - flattened structure for display (constructed internally)
export interface ParsedField {
  name: string;
  value: unknown;
  location: FieldLocation | null;
  type?: string;
  description?: string | null;
  verificationLabel?: string | null;
  required?: boolean;
  path?: string; // Full path for nested fields (e.g., "retentions.retentionName")
  depth?: number; // Nesting depth for indentation
  isEditable?: boolean; // true for primitive types (string, number, boolean) that can be overridden
  coverageTypeName?: string; // CoverageType display name (only set on _coverageLineItem objects)
  coverageTypeId?: number; // CoverageType ID (only set on _coverageLineItem objects)
  lineKey?: string; // Stable server-assigned line identity ("raw-<N>" / "add-<hex>"), set on _coverageLineItem objects
}
